/* This example requires Tailwind CSS v2.0+ */
import { Switch } from '@headlessui/react';
import useLocale from 'next-translate/useTranslation';
import { memo, useEffect, useState } from 'react';

import clsxm from '@/lib/clsxm';

interface ToggleProps {
  initialValue: boolean;
  onChange?: (value: boolean) => void;
  disabled?: boolean;
  isSmall?: boolean;
  cannotBeToggled?: boolean;
}

function Toggle({
  initialValue,
  onChange,
  disabled = false,
  isSmall = false,
  cannotBeToggled = false,
}: ToggleProps) {
  const { t } = useLocale('common');
  const [enabled, setEnabled] = useState(initialValue);

  useEffect(() => {
    setEnabled((current) =>
      current === initialValue ? current : initialValue,
    );
  }, [initialValue]);

  const handleChange = () => {
    onChange && onChange(!enabled);

    if (cannotBeToggled) return;

    if (!disabled) setEnabled(!enabled);
  };

  return (
    <Switch
      checked={enabled}
      onChange={handleChange}
      className={clsxm(
        enabled ? 'bg-primary-600' : 'bg-gray-200',
        'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2',
        isSmall && 'h-4 w-7',
        disabled && 'cursor-not-allowed opacity-50',
      )}
    >
      <span className='sr-only'>{t('Use setting')}</span>
      <span
        aria-hidden='true'
        className={clsxm(
          enabled
            ? isSmall
              ? 'translate-x-3'
              : 'translate-x-5'
            : 'translate-x-0',
          'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
          isSmall && 'h-3 w-3',
        )}
      />
    </Switch>
  );
}

export default memo(Toggle);
